import { useMemo } from "react";
import { LoanProductType } from "./api/types";
import { LoanApplication } from "../contexts";
import { useLoanProductConfigFlags } from "./useLoanProductConfigFlags";

/**
 * Determines whether legacy credit-facility loans (Invoice Discounting/Payable Financing)
 * should show credit-facility specific features.
 * Since these legacy products may no longer carry `showLOCProductsPaymentSchedule`,
 * we also check the corresponding LOC product config flag and enable when either is true.
 */
export const useLoanApplicationLegacyCreditFacilityEnabled = (
  loan: LoanApplication | undefined
) => {
  const loanType = loan?.loanType;

  const productTypePair = useMemo(() => {
    if (loanType === LoanProductType.INVOICE_DISCOUNTING) {
      return [
        LoanProductType.INVOICE_DISCOUNTING,
        LoanProductType.LOC_INVOICE_DISCOUNTING,
      ] as const;
    }

    if (loanType === LoanProductType.PAYABLE_FINANCING) {
      return [
        LoanProductType.PAYABLE_FINANCING,
        LoanProductType.LOC_PAYABLE_FINANCING,
      ] as const;
    }

    return [loanType, loanType] as const;
  }, [loanType]);

  const { showLOCProductsPaymentSchedule: firstTypeFlag } =
    useLoanProductConfigFlags(productTypePair[0]);
  const { showLOCProductsPaymentSchedule: secondTypeFlag } =
    useLoanProductConfigFlags(productTypePair[1]);

  const showLOCProductsPaymentSchedule = firstTypeFlag || secondTypeFlag;

  return useMemo(() => {
    return (
      showLOCProductsPaymentSchedule &&
      loan &&
      (loan.loanType === LoanProductType.INVOICE_DISCOUNTING ||
        loan.loanType === LoanProductType.PAYABLE_FINANCING)
    );
  }, [loan, showLOCProductsPaymentSchedule]);
};
